import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { FilterState } from "../../ProjectList/types";
import type { Database } from "@/utils/supabase.types";

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

const convertToJson = (views: SavedView[]): Json => {
  return views as unknown as Json;
};

type ProjectWithViews = Database["public"]["Tables"]["projects"]["Row"] & {
  views?: SavedView[];
};

type Project = {
  id: string;
  views?: SavedView[];
} & Record<string, string | number | boolean | null | undefined>;

interface ProjectsState {
  [projectId: string]: Project;
}

interface RootState {
  supabaseStore: {
    projects: ProjectsState;
  };
}

export interface SavedView {
  id: string;
  name: string;
  filters: FilterState;
  createdAt: string;
  updatedAt: string;
  isDefault: boolean;
}

export const useViews = () => {
  const dispatch = useDispatch();
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const projects = useSelector(
    (state: RootState) => state.supabaseStore.projects,
  );

  const projectLookup = useMemo(() => {
    return new Map<string, Project>(Object.entries(projects));
  }, [projects]);

  const getViews = useCallback(
    (projectId: string): SavedView[] => {
      const project = projectLookup.get(projectId);
      if (!project?.views) return [];

      try {
        return Array.isArray(project.views) ? project.views : [];
      } catch (error) {
        console.error("Error parsing project views:", error);
        return [];
      }
    },
    [projectLookup],
  );

  const executeDatabaseOperation = useCallback(
    async <T>(
      projectId: string,
      operation: (views: SavedView[]) => SavedView[],
      successMessage: string,
      errorMessage: string,
    ): Promise<T | null> => {
      if (!supabaseClient) {
        toast.error("Database connection not available");
        return null;
      }

      const project = projectLookup.get(projectId);
      if (!project) {
        toast.error("Project not found");
        return null;
      }

      const currentViews = getViews(projectId);
      const updatedViews = operation(currentViews);

      dispatch(
        onProjectsSnapshot({
          newItems: [
            { ...project, views: convertToJson(updatedViews) },
          ] as ProjectWithViews[],
        }),
      );

      try {
        const { error } = await supabaseClient
          .from("projects")
          .update({ views: convertToJson(updatedViews) })
          .eq("id", projectId);

        if (error) {
          console.error(`Database error:`, error);
          toast.error(errorMessage);

          dispatch(
            onProjectsSnapshot({
              newItems: [
                { ...project, views: convertToJson(currentViews) },
              ] as ProjectWithViews[],
            }),
          );

          return null;
        }

        toast.success(successMessage);
        return updatedViews as T;
      } catch (error) {
        console.error(`Operation error:`, error);
        toast.error(errorMessage);

        dispatch(
          onProjectsSnapshot({
            newItems: [
              { ...project, views: convertToJson(currentViews) },
            ] as ProjectWithViews[],
          }),
        );

        return null;
      }
    },
    [supabaseClient, projectLookup, getViews, dispatch],
  );

  const addView = useCallback(
    async (
      projectId: string,
      viewData: Omit<SavedView, "id" | "createdAt" | "updatedAt">,
    ): Promise<SavedView | null> => {
      const newView: SavedView = {
        ...viewData,
        id: uuidv4(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as SavedView;

      const result = await executeDatabaseOperation<SavedView[]>(
        projectId,
        (currentViews) => [...currentViews, newView],
        "View saved successfully",
        "Failed to save view",
      );

      return result ? newView : null;
    },
    [executeDatabaseOperation],
  );

  const deleteView = useCallback(
    async (projectId: string, viewId: string): Promise<boolean> => {
      const result = await executeDatabaseOperation<SavedView[]>(
        projectId,
        (currentViews) => currentViews.filter((view) => view.id !== viewId),
        "View deleted successfully",
        "Failed to delete view",
      );

      return result !== null;
    },
    [executeDatabaseOperation],
  );

  return {
    getViews,
    addView,
    deleteView,
  };
};
